import React from 'react';
import { Box, Typography, Button as MuiButton } from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useAppDataContext } from '../../framework/app/app.data.context';

interface ApplicationApiDocsProps {
  /** 
   * The application key (e.g., 'CI.APPLICATION.KEY', 'cakesales')
   * Will be automatically normalized to match folder naming (dots→underscores, lowercase)
   */
  applicationKey: string;
  /** Optional custom title */
  title?: string;
  /** Optional custom description */
  description?: string;
  /** Height of the iframe (default: 850px) */
  height?: string;
}

/**
 * Normalize application key to match generated folder naming convention
 * Converts dots to underscores and lowercases the entire string
 * Example: 'CI.APPLICATION.KEY' -> 'ci_application_key'
 */
const normalizeApplicationKey = (key: string): string => {
  return key?.replace(/\./g, '_')?.toLowerCase() || '';
};

/**
 * Reusable component to display Swagger API documentation for a specific application
 * in an iframe. Provides a button to copy the bearer token for authentication.
 * 
 * Usage:
 * ```tsx
 * <ApplicationApiDocs 
 *   applicationKey="cakesales"
 *   title="CakeSales API"
 *   description="Analytics and data APIs for the CakeSales application"
 * />
 * ```
 */
export const ApplicationApiDocs: React.FC<ApplicationApiDocsProps> = ({
  applicationKey,
  title,
  description,
  height = '850px'
}) => {
  const context = useAppDataContext();
  const notify = useNotify();
  const apiUrl = context?.apiUrl || 'http://localhost:5002';
  
  // Normalize the application key to match the generated folder/tag naming convention
  // (converts dots to underscores and lowercases)
  const normalizedKey = normalizeApplicationKey(applicationKey);
  
  // Construct the Swagger UI URL for this application
  const apiDocsUrl = `${apiUrl}/api/apps/${normalizedKey}`;
  
  const handleCopyToken = async () => {
    try {
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      if (token) {
        await navigator.clipboard.writeText(token);
        notify('✅ Bearer token copied to clipboard! Paste it in the Swagger "Authorize" dialog.', { type: 'success' });
      } else {
        notify('❌ No auth token available', { type: 'error' });
      }
    } catch (error) {
      console.error('Failed to copy token:', error);
      notify('❌ Failed to copy token', { type: 'error' });
    }
  };
  
  const defaultTitle = `${normalizedKey} API Documentation`;
  const defaultDescription = `REST API endpoints for ${normalizedKey}. Click "Authorize" in Swagger and paste your bearer token to test endpoints.`;
  
  return (
    <Box sx={{ width: '100%', height: `calc(${height} + 100px)` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            {title || defaultTitle}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {description || defaultDescription}
          </Typography>
        </Box>
        <MuiButton
          onClick={handleCopyToken}
          startIcon={<CodeIcon />}
          variant="contained"
          color="success"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Copy Bearer Token
        </MuiButton>
      </Box>
      <iframe
        src={apiDocsUrl}
        width="100%"
        height={height}
        style={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}
        title={`${normalizedKey} API Documentation`}
      />
    </Box>
  );
};

export default ApplicationApiDocs;

